import { api } from "./api";

/** Web Push on this device: register the service worker, subscribe with the server's VAPID key, store it. */
export const pushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

const b64ToBytes = (s: string) => {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const raw = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

export async function registerSW() {
  if (!("serviceWorker" in navigator)) return null;
  return navigator.serviceWorker.register("/sw.js");
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration("/");
  return (await reg?.pushManager.getSubscription()) ?? null;
}

export async function enablePush(): Promise<"ok" | "denied" | "unsupported" | "disabled"> {
  if (!pushSupported()) return "unsupported";
  const cfg = await api<{ enabled: boolean; publicKey: string | null }>("/api/push/config");
  if (!cfg.enabled || !cfg.publicKey) return "disabled";
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return "denied";
  const reg = (await registerSW()) ?? (await navigator.serviceWorker.ready);
  await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: b64ToBytes(cfg.publicKey),
    }));
  const json = sub.toJSON();
  await api("/api/push/subscribe", { method: "POST", json: { endpoint: sub.endpoint, keys: json.keys } });
  return "ok";
}

export async function disablePush() {
  const sub = await currentSubscription();
  if (!sub) return;
  await api("/api/push/subscribe", { method: "DELETE", json: { endpoint: sub.endpoint } }).catch(() => {});
  await sub.unsubscribe().catch(() => {});
}
